import * as THREE from 'three';
import { MAP, ROAD_ASPHALT_METERS_PER_TILE } from '../utils/constants';
import { districtFromCell, districtStyle } from './districts';
import { addLandmarks } from './landmarks';
import { invalidateBuildingAABBCache } from './collision';
import {
  BUILDING_LOT_PAD,
  getBlockCellOuterBounds,
  getTowerFootprintBounds,
  isParkLot,
} from './mapLayout';
import { mulberry32 } from '../utils/rng';
import { createDestructibles } from './destructibles';
import { getFacadeEmissiveTexture } from './buildingFacades';
import {
  BUILDING_PROCEDURAL_VARIANTS,
  createProceduralTowerGeometryPair,
} from './proceduralBuildingMasses';
import {
  addSidewalksForBankedPath,
  ribbonBankedFromCenterlinePath,
  sampleCurvePath,
  sampleWidthsAndBanks,
} from './curvedRoads';
import { addJumpRampMeshes } from './ramps';
import {
  createAsphaltRoughnessTexture,
  createCrossWalkTexture,
  createManholeTexture,
  createRoadDashMarkingTextures,
  createRoadEdgeMarkingTextures,
  ROAD_DASH_TEXTURE_PERIOD_M,
} from './proceduralTextures';


let asphaltRoughnessShared: THREE.CanvasTexture | null = null;
function getAsphaltRoughnessMap(): THREE.CanvasTexture {
  if (!asphaltRoughnessShared) {
    asphaltRoughnessShared = createAsphaltRoughnessTexture();
  }
  return asphaltRoughnessShared;
}

function roughRepeat(rx: number, ry: number): THREE.CanvasTexture {
  const t = getAsphaltRoughnessMap().clone();
  t.repeat.set(rx, ry);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

const textureLoader = new THREE.TextureLoader();
const realTextureCache: Map<string, THREE.Texture> = new Map();

function loadTextureRect(
  path: string,
  repeatX: number,
  repeatY: number,
  colorSpace: THREE.ColorSpace,
  cacheTag: string,
): THREE.Texture {
  const key = `${path}-${repeatX}x${repeatY}-${cacheTag}`;
  if (realTextureCache.has(key)) return realTextureCache.get(key)!;

  const tex = textureLoader.load(path);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeatX, repeatY);
  tex.colorSpace = colorSpace;
  tex.anisotropy = 16;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  realTextureCache.set(key, tex);
  return tex;
}

function loadRealTexture(path: string, repeat: number = 1): THREE.Texture {
  return loadTextureRect(path, repeat, repeat, THREE.SRGBColorSpace, 'srgb');
}

function loadRealNormalMap(path: string, repeat: number = 1): THREE.Texture {
  return loadTextureRect(path, repeat, repeat, THREE.NoColorSpace, 'nor');
}

/** Poly Haven asphalt_02 (CC0) — https://polyhaven.com/a/asphalt_02 — 4K JPG set baked into public/. */
const ASPHALT_02_DIR = '/assets/textures/asphalt_02';

function loadAsphalt02RoadSet(
  repeatX: number,
  repeatY: number,
): {
  map: THREE.Texture;
  normalMap: THREE.Texture;
  roughnessMap: THREE.Texture;
  aoMap: THREE.Texture;
} {
  return {
    map: loadTextureRect(
      `${ASPHALT_02_DIR}/asphalt_02_diff_4k.jpg`,
      repeatX,
      repeatY,
      THREE.SRGBColorSpace,
      'ph02diff',
    ),
    normalMap: loadTextureRect(
      `${ASPHALT_02_DIR}/asphalt_02_nor_gl_4k.jpg`,
      repeatX,
      repeatY,
      THREE.NoColorSpace,
      'ph02nor',
    ),
    roughnessMap: loadTextureRect(
      `${ASPHALT_02_DIR}/asphalt_02_rough_4k.jpg`,
      repeatX,
      repeatY,
      THREE.NoColorSpace,
      'ph02rough',
    ),
    aoMap: loadTextureRect(
      `${ASPHALT_02_DIR}/asphalt_02_ao_4k.jpg`,
      repeatX,
      repeatY,
      THREE.NoColorSpace,
      'ph02ao',
    ),
  };
}

function makeAsphaltRoadMaterial(
  tex: ReturnType<typeof loadAsphalt02RoadSet>,
  wetness: number = 0,
): THREE.MeshStandardMaterial {
  const baseRoughness = 0.92 - wetness * 0.35;
  const baseMetalness = wetness * 0.08;
  const envIntensity = 0.42 + wetness * 0.55;

  return new THREE.MeshStandardMaterial({
    color: wetness > 0.3 ? 0xe8e8e8 : 0xffffff,
    map: tex.map,
    normalMap: tex.normalMap,
    normalScale: new THREE.Vector2(1.4 + wetness * 0.3, 1.4 + wetness * 0.3),
    roughnessMap: tex.roughnessMap,
    roughness: baseRoughness,
    metalness: baseMetalness,
    aoMap: tex.aoMap,
    aoMapIntensity: 1.15,
    envMapIntensity: envIntensity,
  });
}

/** Road planes use aoMap — requires uv2 matching uv1 (Three.js standard). */
function roadPlaneGeometry(width: number, height: number): THREE.PlaneGeometry {
  const g = new THREE.PlaneGeometry(width, height);
  g.setAttribute('uv2', g.attributes.uv.clone());
  return g;
}

type BuildingPBRSet = {
  map: THREE.Texture;
  normalMap: THREE.Texture;
  roughnessMap: THREE.Texture;
  aoMap: THREE.Texture;
};

const buildingPBRCache: Map<string, BuildingPBRSet> = new Map();

function loadBuildingPBRSet(
  base: string,
  repeatX: number,
  repeatY: number,
): BuildingPBRSet {
  const key = `${base}-${repeatX}x${repeatY}`;
  const hit = buildingPBRCache.get(key);
  if (hit) return hit;

  const set: BuildingPBRSet = {
    map: loadTextureRect(
      `/assets/textures/${base}_diff.jpg`,
      repeatX,
      repeatY,
      THREE.SRGBColorSpace,
      'bdiff',
    ),
    normalMap: loadTextureRect(
      `/assets/textures/${base}_nor.jpg`,
      repeatX,
      repeatY,
      THREE.NoColorSpace,
      'bnor',
    ),
    roughnessMap: loadTextureRect(
      `/assets/textures/${base}_rough.jpg`,
      repeatX,
      repeatY,
      THREE.NoColorSpace,
      'brough',
    ),
    aoMap: loadTextureRect(
      `/assets/textures/${base}_ao.jpg`,
      repeatX,
      repeatY,
      THREE.NoColorSpace,
      'bao',
    ),
  };
  buildingPBRCache.set(key, set);
  return set;
}

let cachedCrosswalkTex: THREE.CanvasTexture | null = null;
function getCrosswalkTexture(): THREE.CanvasTexture {
  if (!cachedCrosswalkTex) {
    cachedCrosswalkTex = createCrossWalkTexture();
  }
  return cachedCrosswalkTex;
}

let cachedManholeTex: THREE.CanvasTexture | null = null;
function getManholeTexture(): THREE.CanvasTexture {
  if (!cachedManholeTex) {
    cachedManholeTex = createManholeTexture();
  }
  return cachedManholeTex;
}

function addBuildingsInstanced(
  root: THREE.Group,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
  rng: () => number,
): void {
  const maxLots = grid * grid;
  let parkLots = 0;
  for (let iz = 0; iz < grid; iz++) {
    for (let ix = 0; ix < grid; ix++) {
      if (isParkLot(ix, iz)) parkLots++;
    }
  }
  const count = maxLots - parkLots;

  const pbrRoof = loadBuildingPBRSet('building_concrete', 3.5, 3.5);

  /** Poly Haven–style facade sets (local JPGs; replace via `public/assets/textures/polyhaven/README.md`). */
  const facadeModern = loadBuildingPBRSet('building_concrete', 2.0, 5.8);
  const facadeClassic = loadBuildingPBRSet('building_brick', 2.4, 5.2);
  const facadeIndustrial = loadBuildingPBRSet('building_metal', 2.7, 4.6);

  const windowEmissive = getFacadeEmissiveTexture();

  const makeFacadeMat = (
    facade: BuildingPBRSet,
    style: 'modern' | 'classic' | 'industrial',
    emRepeatX: number,
    emRepeatY: number,
  ): THREE.MeshPhysicalMaterial => {
    const winEm = windowEmissive.clone();
    winEm.repeat.set(emRepeatX, emRepeatY);
    winEm.wrapS = winEm.wrapT = THREE.RepeatWrapping;

    const emissiveIntensity =
      style === 'modern' ? 1.02 : style === 'classic' ? 0.7 : 0.48;
    const metalness =
      style === 'modern' ? 0.07 : style === 'classic' ? 0.05 : 0.24;
    const rough =
      style === 'modern' ? 0.68 : style === 'classic' ? 0.84 : 0.55;
    const clear = style === 'modern' ? 0.22 : style === 'classic' ? 0.08 : 0.05;

    const mat = new THREE.MeshPhysicalMaterial({
      map: facade.map,
      normalMap: facade.normalMap,
      normalScale: new THREE.Vector2(0.78, 0.78),
      roughnessMap: facade.roughnessMap,
      roughness: rough,
      metalness,
      aoMap: facade.aoMap,
      aoMapIntensity: 0.95,
      envMapIntensity: style === 'modern' ? 0.68 : style === 'classic' ? 0.52 : 0.58,
      emissiveMap: winEm,
      emissive: new THREE.Color(0xffe8d4),
      emissiveIntensity,
      clearcoat: clear,
      clearcoatRoughness: 0.2,
      side: THREE.FrontSide,
    });
    mat.needsUpdate = true;
    return mat;
  };

  const materials: THREE.MeshPhysicalMaterial[] = [
    makeFacadeMat(facadeModern, 'modern', 2.05, 6.35),
    makeFacadeMat(facadeClassic, 'classic', 2.45, 5.45),
    makeFacadeMat(facadeIndustrial, 'industrial', 2.75, 4.85),
  ];

  const makeTrimMat = (
    style: 'modern' | 'classic' | 'industrial',
  ): THREE.MeshStandardMaterial => {
    const color =
      style === 'modern'
        ? 0x4a515a
        : style === 'classic'
          ? 0x4d3f3a
          : 0x3a4048;
    return new THREE.MeshStandardMaterial({
      color,
      roughness: style === 'industrial' ? 0.52 : 0.76,
      metalness: style === 'industrial' ? 0.48 : 0.22,
      envMapIntensity: 0.48,
    });
  };

  const trimMats = [
    makeTrimMat('modern'),
    makeTrimMat('classic'),
    makeTrimMat('industrial'),
  ];

  const towerPairs = Array.from({ length: BUILDING_PROCEDURAL_VARIANTS }, (_, v) =>
    createProceduralTowerGeometryPair(v),
  );
  const maxInst = Math.ceil(count * 3) + 32;

  const meshes: THREE.InstancedMesh[] = [];
  for (let v = 0; v < BUILDING_PROCEDURAL_VARIANTS; v++) {
    const pair = towerPairs[v]!;
    for (let s = 0; s < 3; s++) {
      const mf = new THREE.InstancedMesh(pair.facade, materials[s]!, maxInst);
      const mt = new THREE.InstancedMesh(pair.trim, trimMats[s]!, maxInst);
      for (const m of [mf, mt]) {
        m.castShadow = true;
        m.receiveShadow = true;
        m.count = 0;
      }
      meshes.push(mf, mt);
    }
  }

  const dummy = new THREE.Object3D();
  const c = new THREE.Color();
  const pad = BUILDING_LOT_PAD;
  const indices = new Array(BUILDING_PROCEDURAL_VARIANTS * 3).fill(0);

  const rooftopGeo = new THREE.BoxGeometry(1, 1, 1);
  rooftopGeo.setAttribute('uv2', rooftopGeo.attributes.uv.clone());
  const rooftopMat = new THREE.MeshPhysicalMaterial({
    map: pbrRoof.map,
    normalMap: pbrRoof.normalMap,
    normalScale: new THREE.Vector2(0.55, 0.55),
    roughnessMap: pbrRoof.roughnessMap,
    aoMap: pbrRoof.aoMap,
    aoMapIntensity: 0.85,
    color: 0x3a3f48,
    roughness: 1,
    metalness: 0.42,
    envMapIntensity: 0.42,
    clearcoat: 0.08,
    clearcoatRoughness: 0.55,
  });
  const rooftopDetails: THREE.Object3D[] = [];

  for (let iz = 0; iz < grid; iz++) {
    for (let ix = 0; ix < grid; ix++) {
      if (isParkLot(ix, iz)) continue;
      const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;
      const cx = -half + roadWithSidewalk + blockW / 2 + ix * (blockW + roadWithSidewalk);
      const cz = -half + roadWithSidewalk + blockW / 2 + iz * (blockW + roadWithSidewalk);
      const w = blockW - pad;
      const d = blockW - pad;
      const district = districtFromCell(cx, cz, half);
      const sty = districtStyle(district);
      const height = sty.hMin + rng() * (sty.hMax - sty.hMin);

      let styleIdx = 0;
      if (district === 'industrial') styleIdx = 2;
      else if (district === 'suburbs' || district === 'docks') styleIdx = 1;
      else styleIdx = Math.floor(rng() * 2);

      const variantIdx = Math.floor(rng() * BUILDING_PROCEDURAL_VARIANTS);

      let useStack = false;
      if (height > 16) {
        if (district === 'downtown' || district === 'highway') {
          useStack = rng() > 0.06;
        } else if (district === 'industrial') {
          useStack = rng() > 0.12;
        } else if (district === 'docks') {
          useStack = height > 18 && rng() > 0.4;
        } else if (district === 'suburbs') {
          useStack = height > 20 && rng() > 0.55;
        } else {
          useStack = rng() > 0.22;
        }
      }

      c.setRGB(0.94, 0.95, 0.97);
      c.lerp(new THREE.Color(0.9, 0.93, 0.99), sty.cool * 0.14);
      if (district === 'downtown') c.offsetHSL(0, 0.02, 0.025);
      if (district === 'industrial') c.offsetHSL(0.015, -0.04, -0.05);
      c.multiplyScalar(0.97 + sty.emissive * 0.12);

      const pushPart = (px: number, py: number, pz: number, sx: number, sy: number, sz: number) => {
        dummy.position.set(px, py, pz);
        dummy.scale.set(sx, sy, sz);
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();

        const slot = variantIdx * 3 + styleIdx;
        const base = slot * 2;
        const ii = indices[slot]!;
        meshes[base]!.setMatrixAt(ii, dummy.matrix);
        meshes[base + 1]!.setMatrixAt(ii, dummy.matrix);
        meshes[base]!.setColorAt(ii, c);
        meshes[base + 1]!.setColorAt(ii, c);
        indices[slot]!++;
        const n = indices[slot]!;
        meshes[base]!.count = n;
        meshes[base + 1]!.count = n;
      };

      let roofW = w;
      let roofD = d;
      if (!useStack) {
        pushPart(cx, height * 0.5, cz, w, height, d);
      } else {
        const podiumH = THREE.MathUtils.clamp(
          height * (0.21 + rng() * 0.09),
          4.2,
          13.5,
        );
        const towerH = height - podiumH;
        const inset = 0.83 + rng() * 0.11;
        const tw = w * inset;
        const td = d * inset;
        pushPart(cx, podiumH * 0.5, cz, w, podiumH, d);
        pushPart(cx, podiumH + towerH * 0.5, cz, tw, towerH, td);
        roofW = tw;
        roofD = td;
        if (height > 21 && rng() > 0.2) {
          const ph = 0.65 + rng() * 1.1;
          const corn = 1.01 + rng() * 0.03;
          pushPart(cx, height - ph * 0.5, cz, tw * corn, ph, td * corn);
        }
      }

      if (height > 20) {
        const numAcUnits = 1 + Math.floor(rng() * 3);
        for (let i = 0; i < numAcUnits; i++) {
          const acW = w * (0.08 + rng() * 0.1);
          const acH = 1.5 + rng() * 2;
          const acD = d * (0.08 + rng() * 0.1);
          const ac = new THREE.Mesh(rooftopGeo, rooftopMat);
          ac.scale.set(acW, acH, acD);
          ac.position.set(
            cx + (rng() - 0.5) * roofW * 0.78,
            height + acH * 0.5,
            cz + (rng() - 0.5) * roofD * 0.78,
          );
          ac.castShadow = true;
          rooftopDetails.push(ac);
        }
      }

      if (height > 35 && rng() > 0.4) {
        const waterTankH = 4 + rng() * 3;
        const waterTankR = 1.5 + rng() * 1;
        const waterTank = new THREE.Mesh(
          new THREE.CylinderGeometry(waterTankR, waterTankR, waterTankH, 12),
          rooftopMat,
        );
        waterTank.position.set(
          cx + (rng() - 0.5) * roofW * 0.42,
          height + waterTankH * 0.5,
          cz + (rng() - 0.5) * roofD * 0.42,
        );
        waterTank.castShadow = true;
        rooftopDetails.push(waterTank);
        
        const tankLegs = 4;
        for (let leg = 0; leg < tankLegs; leg++) {
          const angle = (leg / tankLegs) * Math.PI * 2;
          const legMesh = new THREE.Mesh(
            new THREE.CylinderGeometry(0.15, 0.15, 2, 4),
            rooftopMat,
          );
          legMesh.position.set(
            waterTank.position.x + Math.cos(angle) * (waterTankR - 0.3),
            height + 1,
            waterTank.position.z + Math.sin(angle) * (waterTankR - 0.3),
          );
          rooftopDetails.push(legMesh);
        }
      }

      if (height > 50 && rng() > 0.5) {
        const antennaH = 8 + rng() * 15;
        const antenna = new THREE.Mesh(
          new THREE.CylinderGeometry(0.15, 0.25, antennaH, 6),
          rooftopMat,
        );
        antenna.position.set(cx, height + antennaH * 0.5, cz);
        antenna.castShadow = true;
        rooftopDetails.push(antenna);
        
        if (rng() > 0.5) {
          const light = new THREE.Mesh(
            new THREE.SphereGeometry(0.28, 10, 10),
            new THREE.MeshPhysicalMaterial({
              color: 0x330000,
              emissive: 0xff1100,
              emissiveIntensity: 2.2,
              roughness: 0.35,
              metalness: 0,
            }),
          );
          light.position.set(cx, height + antennaH, cz);
          rooftopDetails.push(light);
        }
      }

      if (height > 60 && rng() > 0.7) {
        const spireH = 10 + rng() * 20;
        const spire = new THREE.Mesh(
          new THREE.ConeGeometry(1.5, spireH, 8),
          new THREE.MeshPhysicalMaterial({
            color: 0x888888,
            metalness: 0.9,
            roughness: 0.2,
            envMapIntensity: 1.5,
          }),
        );
        spire.position.set(cx, height + spireH * 0.5, cz);
        spire.castShadow = true;
        rooftopDetails.push(spire);
      }
    }
  }

  for (const mesh of meshes) {
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    root.add(mesh);
  }

  for (const detail of rooftopDetails) {
    root.add(detail);
  }
}

function addRoadCenterStripes(
  root: THREE.Group,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
): void {
  const laneW = 0.13;
  const y = 0.052;
  const span = MAP.SIZE - 40;
  const dashRepeat = span / ROAD_DASH_TEXTURE_PERIOD_M;
  const edgeRepeatAlong = span / 5.5;

  const dashMark = createRoadDashMarkingTextures();
  const cloneDashRepeat = (rx: number, ry: number) => {
    const map = dashMark.map.clone();
    const roughnessMap = dashMark.roughnessMap.clone();
    const bumpMap = dashMark.bumpMap.clone();
    for (const t of [map, roughnessMap, bumpMap]) {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(rx, ry);
    }
    return { map, roughnessMap, bumpMap };
  };
  const dashH = cloneDashRepeat(dashRepeat, 1);
  const dashV = cloneDashRepeat(1, dashRepeat);

  const mkStripe = (d: {
    map: THREE.Texture;
    roughnessMap: THREE.Texture;
    bumpMap: THREE.Texture;
  }) =>
    new THREE.MeshStandardMaterial({
      map: d.map,
      roughnessMap: d.roughnessMap,
      bumpMap: d.bumpMap,
      bumpScale: 0.06,
      color: 0xffffff,
      transparent: true,
      depthWrite: false,
      roughness: 1,
      metalness: 0.02,
      envMapIntensity: 0.28,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -2,
    });

  const stripeMatH = mkStripe(dashH);
  const stripeMatV = mkStripe(dashV);

  const edgeMark = createRoadEdgeMarkingTextures();
  const cloneEdgeRepeat = (rx: number, ry: number) => {
    const map = edgeMark.map.clone();
    const roughnessMap = edgeMark.roughnessMap.clone();
    const bumpMap = edgeMark.bumpMap.clone();
    for (const t of [map, roughnessMap, bumpMap]) {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(rx, ry);
    }
    return { map, roughnessMap, bumpMap };
  };
  const edgeH = cloneEdgeRepeat(edgeRepeatAlong, 1);
  const edgeV = cloneEdgeRepeat(1, edgeRepeatAlong);

  const mkEdge = (d: {
    map: THREE.Texture;
    roughnessMap: THREE.Texture;
    bumpMap: THREE.Texture;
  }) =>
    new THREE.MeshStandardMaterial({
      map: d.map,
      roughnessMap: d.roughnessMap,
      bumpMap: d.bumpMap,
      bumpScale: 0.055,
      color: 0xffffff,
      roughness: 1,
      metalness: 0.02,
      envMapIntensity: 0.26,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -2,
    });

  const edgeMatH = mkEdge(edgeH);
  const edgeMatV = mkEdge(edgeV);

  const edgeLineW = 0.11;
  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;

  for (let i = 0; i <= grid; i++) {
    const z = -half + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;

    const centerLine = new THREE.Mesh(
      new THREE.PlaneGeometry(span, laneW),
      stripeMatH,
    );
    centerLine.rotation.x = -Math.PI / 2;
    centerLine.position.set(0, y, z);
    root.add(centerLine);

    const edgeLeft = new THREE.Mesh(
      new THREE.PlaneGeometry(span, edgeLineW),
      edgeMatH,
    );
    edgeLeft.rotation.x = -Math.PI / 2;
    edgeLeft.position.set(0, y, z - roadWidth / 2 + edgeLineW);
    root.add(edgeLeft);

    const edgeRight = new THREE.Mesh(
      new THREE.PlaneGeometry(span, edgeLineW),
      edgeMatH,
    );
    edgeRight.rotation.x = -Math.PI / 2;
    edgeRight.position.set(0, y, z + roadWidth / 2 - edgeLineW);
    root.add(edgeRight);
  }

  for (let j = 0; j <= grid; j++) {
    const x = -half + j * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;

    const centerLine = new THREE.Mesh(
      new THREE.PlaneGeometry(laneW, span),
      stripeMatV,
    );
    centerLine.rotation.x = -Math.PI / 2;
    centerLine.position.set(x, y, 0);
    root.add(centerLine);

    const edgeLeft = new THREE.Mesh(
      new THREE.PlaneGeometry(edgeLineW, span),
      edgeMatV,
    );
    edgeLeft.rotation.x = -Math.PI / 2;
    edgeLeft.position.set(x - roadWidth / 2 + edgeLineW, y, 0);
    root.add(edgeLeft);

    const edgeRight = new THREE.Mesh(
      new THREE.PlaneGeometry(edgeLineW, span),
      edgeMatV,
    );
    edgeRight.rotation.x = -Math.PI / 2;
    edgeRight.position.set(x + roadWidth / 2 - edgeLineW, y, 0);
    root.add(edgeRight);
  }
}

/** Warm fill under elevated decks — emissive panels, no PointLights. */
function addUnderpassFillLights(_root: THREE.Group, _half: number): void {
  // Removed PointLights for performance. Underpass mood comes from ambient + emissive geo.
}

/** Soft district-colored points — removed for performance. */
function addDistrictAmbientLights(_root: THREE.Group, _half: number): void {
  // Removed PointLights for performance.
}

function addCornerLamps(root: THREE.Group, half: number): void {
  const poleMat = new THREE.MeshStandardMaterial({
    color: 0x5a6470,
    roughness: 0.68,
    metalness: 0.35,
    envMapIntensity: 0.48,
  });
  const armMat = new THREE.MeshStandardMaterial({
    color: 0x4a5460,
    roughness: 0.62,
    metalness: 0.42,
    envMapIntensity: 0.45,
  });
  const headHousing = new THREE.MeshStandardMaterial({
    color: 0x2a3038,
    roughness: 0.55,
    metalness: 0.35,
  });
  const lensMat = new THREE.MeshStandardMaterial({
    color: 0xfff8e8,
    emissive: 0xffeedd,
    emissiveIntensity: 0.55,
    roughness: 0.22,
    metalness: 0.08,
  });
  const o = half * 0.72;
  for (const [x, z] of [
    [-o, -o],
    [o, -o],
    [-o, o],
    [o, o],
  ] as [number, number][]) {
    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.28, 0.4, 12.5, 8),
      poleMat,
    );
    pole.position.set(x, 6.25, z);
    pole.castShadow = true;
    root.add(pole);
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55, 0.6, 0.35, 8),
      poleMat,
    );
    base.position.set(x, 0.175, z);
    base.castShadow = true;
    root.add(base);

    const armY = 12.4;
    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 4.2, 8), armMat);
    arm.rotation.z = Math.PI / 2;
    arm.position.set(x + 1.9, armY, z);
    arm.castShadow = true;
    root.add(arm);

    for (const off of [-0.85, 0.85]) {
      const hood = new THREE.Mesh(
        new THREE.BoxGeometry(1.1, 0.35, 0.55),
        headHousing,
      );
      hood.position.set(x + 3.15 + off * 0.05, armY + 0.1, z);
      hood.castShadow = true;
      root.add(hood);
      const lens = new THREE.Mesh(
        new THREE.SphereGeometry(0.22, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.5),
        lensMat,
      );
      lens.rotation.x = Math.PI;
      lens.position.set(x + 3.15 + off * 0.05, armY - 0.02, z + 0.22);
      root.add(lens);
    }

    const mastCap = new THREE.Mesh(
      new THREE.SphereGeometry(0.32, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.5),
      armMat,
    );
    mastCap.position.set(x, 12.55, z);
    root.add(mastCap);
  }
}

/** prd2 Day 4 — banked ribbons + variable width; district-scoped splines. */
function addCurvedBoulevards(
  root: THREE.Group,
  half: number,
  roadWidth: number,
  boulevardRoadMat: THREE.MeshStandardMaterial,
  boulevardSwMat: THREE.MeshStandardMaterial,
): void {
  const h = half;
  const segments = 280;
  const roadW = roadWidth - 6;
  const laneW = Math.max(4.5, roadWidth * 0.11);
  const swW = Math.max(3, roadWidth * 0.36);
  const roadOff = 0.052;
  const stripeY = 0.058;
  const swY = 0.028;
  const sideOff = roadWidth * 0.5 + swW * 0.5;

  const strips: {
    curve: THREE.CatmullRomCurve3;
    w: number[];
    b: number[];
  }[] = [
    {
      curve: new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(-h * 0.92, 0, -h * 0.38),
          new THREE.Vector3(-h * 0.62, 0, -h * 0.12),
          new THREE.Vector3(-h * 0.42, 0, h * 0.05),
          new THREE.Vector3(-h * 0.12, 0, h * 0.02),
          new THREE.Vector3(h * 0.08, 0, -h * 0.18),
          new THREE.Vector3(h * 0.35, 0, -h * 0.05),
          new THREE.Vector3(h * 0.52, 0, h * 0.12),
          new THREE.Vector3(h * 0.72, 0, h * 0.32),
          new THREE.Vector3(h * 0.9, 0, h * 0.45),
        ],
        false,
        'centripetal',
      ),
      w: [20, 17, 14, 11, 10, 12, 15, 17, 20],
      b: [0, 3, 7, 11, 14, 10, 5, 2, 0],
    },
    {
      curve: new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(-h * 0.18, 0, -h * 0.92),
          new THREE.Vector3(h * 0.28, 0, -h * 0.35),
          new THREE.Vector3(-h * 0.22, 0, h * 0.22),
          new THREE.Vector3(h * 0.35, 0, h * 0.88),
        ],
        false,
        'centripetal',
      ),
      w: [21, 14, 12, 19],
      b: [0, 9, 12, 0],
    },
    {
      curve: new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(-h * 0.88, 0, h * 0.52),
          new THREE.Vector3(-h * 0.72, 0, h * 0.38),
          new THREE.Vector3(-h * 0.58, 0, h * 0.22),
          new THREE.Vector3(-h * 0.62, 0, h * 0.02),
          new THREE.Vector3(-h * 0.78, 0, -h * 0.08),
          new THREE.Vector3(-h * 0.92, 0, -h * 0.02),
        ],
        false,
        'centripetal',
      ),
      w: [22, 20, 18, 18, 20, 22],
      b: [0, 1, 2, 2, 1, 0],
    },
    {
      curve: new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(-h * 0.18, 0, h * 0.08),
          new THREE.Vector3(-h * 0.05, 0, h * 0.05),
          new THREE.Vector3(h * 0.12, 0, h * 0.02),
          new THREE.Vector3(h * 0.22, 0, -h * 0.08),
          new THREE.Vector3(h * 0.08, 0, -h * 0.18),
          new THREE.Vector3(-h * 0.12, 0, -h * 0.12),
          new THREE.Vector3(-h * 0.28, 0, -h * 0.02),
        ],
        false,
        'centripetal',
      ),
      w: [14, 11, 8, 7, 8, 11, 14],
      b: [0, 5, 11, 15, 11, 5, 0],
    },
    {
      curve: new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(-h * 0.42, 0, -h * 0.72),
          new THREE.Vector3(-h * 0.18, 0, -h * 0.62),
          new THREE.Vector3(h * 0.08, 0, -h * 0.58),
          new THREE.Vector3(h * 0.38, 0, -h * 0.52),
          new THREE.Vector3(h * 0.62, 0, -h * 0.42),
          new THREE.Vector3(h * 0.78, 0, -h * 0.22),
        ],
        false,
        'centripetal',
      ),
      w: [20, 17, 15, 16, 18, 22],
      b: [0, 3, 4, 4, 3, 0],
    },
    {
      curve: new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(h * 0.55, 0, h * 0.62),
          new THREE.Vector3(h * 0.42, 0, h * 0.38),
          new THREE.Vector3(h * 0.18, 0, h * 0.18),
          new THREE.Vector3(-h * 0.12, 0, h * 0.05),
          new THREE.Vector3(-h * 0.38, 0, -h * 0.08),
          new THREE.Vector3(-h * 0.55, 0, -h * 0.22),
          new THREE.Vector3(-h * 0.48, 0, -h * 0.48),
          new THREE.Vector3(-h * 0.22, 0, -h * 0.62),
        ],
        false,
        'centripetal',
      ),
      w: [24, 20, 16, 12, 10, 12, 17, 22],
      b: [0, 4, 8, 13, 15, 11, 5, 0],
    },
    {
      curve: new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(-h * 0.55, 0, -h * 0.38),
          new THREE.Vector3(-h * 0.42, 0, -h * 0.52),
          new THREE.Vector3(-h * 0.28, 0, -h * 0.62),
          new THREE.Vector3(h * 0.02, 0, -h * 0.68),
          new THREE.Vector3(h * 0.32, 0, -h * 0.58),
          new THREE.Vector3(h * 0.52, 0, -h * 0.42),
          new THREE.Vector3(h * 0.62, 0, -h * 0.22),
        ],
        false,
        'centripetal',
      ),
      w: [18, 16, 14, 12, 14, 16, 18],
      b: [0, 3, 7, 9, 7, 3, 0],
    },
    {
      curve: new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(-h * 0.72, 0, -h * 0.82),
          new THREE.Vector3(-h * 0.55, 0, -h * 0.68),
          new THREE.Vector3(-h * 0.38, 0, -h * 0.55),
          new THREE.Vector3(-h * 0.22, 0, -h * 0.42),
          new THREE.Vector3(-h * 0.08, 0, -h * 0.28),
          new THREE.Vector3(h * 0.12, 0, -h * 0.18),
        ],
        false,
        'centripetal',
      ),
      w: [20, 17, 14, 12, 13, 16],
      b: [0, 4, 9, 13, 10, 0],
    },
    {
      curve: new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(h * 0.82, 0, -h * 0.72),
          new THREE.Vector3(h * 0.62, 0, -h * 0.55),
          new THREE.Vector3(h * 0.42, 0, -h * 0.38),
          new THREE.Vector3(h * 0.22, 0, -h * 0.22),
          new THREE.Vector3(h * 0.05, 0, -h * 0.08),
          new THREE.Vector3(-h * 0.15, 0, h * 0.02),
          new THREE.Vector3(-h * 0.38, 0, h * 0.12),
        ],
        false,
        'centripetal',
      ),
      w: [22, 19, 15, 12, 11, 14, 20],
      b: [0, 2, 6, 11, 14, 8, 0],
    },
  ];

  const roadSurf = boulevardRoadMat;
  const swMat = boulevardSwMat;
  const boulevardDashMark = createRoadDashMarkingTextures();
  const boulevardDashRepeat =
    ROAD_ASPHALT_METERS_PER_TILE / ROAD_DASH_TEXTURE_PERIOD_M;

  for (const strip of strips) {
    const path = sampleCurvePath(strip.curve, segments);
    const { widthAt, bankAt } = sampleWidthsAndBanks(
      strip.curve,
      segments,
      strip.w,
      strip.b,
    );
    const scaledW = widthAt.map((w) => (w / 14) * roadW);
    const roadGeo = ribbonBankedFromCenterlinePath(path, scaledW, bankAt, roadOff);
    const roadMesh = new THREE.Mesh(roadGeo, roadSurf);
    roadMesh.receiveShadow = true;
    root.add(roadMesh);

    const stripeW = widthAt.map((w) => (w / 14) * laneW);
    const stripeGeo = ribbonBankedFromCenterlinePath(path, stripeW, bankAt, stripeY);
    const map = boulevardDashMark.map.clone();
    const roughnessMap = boulevardDashMark.roughnessMap.clone();
    const bumpMap = boulevardDashMark.bumpMap.clone();
    for (const t of [map, roughnessMap, bumpMap]) {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(boulevardDashRepeat, 1);
    }
    const stripeMat = new THREE.MeshStandardMaterial({
      map,
      roughnessMap,
      bumpMap,
      bumpScale: 0.06,
      color: 0xffffff,
      transparent: true,
      depthWrite: false,
      roughness: 1,
      metalness: 0.02,
      envMapIntensity: 0.28,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -2,
    });
    const stripeMesh = new THREE.Mesh(stripeGeo, stripeMat);
    stripeMesh.receiveShadow = true;
    root.add(stripeMesh);

    addSidewalksForBankedPath(root, path, sideOff, swW, swY, swMat);
  }
}

function addCurbs(
  root: THREE.Group,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
): void {
  const curbMat = new THREE.MeshStandardMaterial({
    color: 0x888888,
    roughness: 0.8,
    metalness: 0.02,
  });
  const curbH = 0.12;
  const curbW = 0.15;
  const edgePad = 12;
  const span = MAP.SIZE - edgePad;
  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;

  for (let i = 0; i <= grid; i++) {
    const z = -half + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;

    for (const side of [-1, 1]) {
      const curb = new THREE.Mesh(
        new THREE.BoxGeometry(span, curbH, curbW),
        curbMat,
      );
      curb.position.set(0, curbH / 2 + 0.08, z + side * (roadWidth / 2 + MAP.SIDEWALK_WIDTH));
      curb.receiveShadow = true;
      curb.castShadow = true;
      root.add(curb);
    }
  }

  for (let j = 0; j <= grid; j++) {
    const x = -half + j * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;

    for (const side of [-1, 1]) {
      const curb = new THREE.Mesh(
        new THREE.BoxGeometry(curbW, curbH, span),
        curbMat,
      );
      curb.position.set(x + side * (roadWidth / 2 + MAP.SIDEWALK_WIDTH), curbH / 2 + 0.08, 0);
      curb.receiveShadow = true;
      curb.castShadow = true;
      root.add(curb);
    }
  }
}

function addCrosswalks(
  root: THREE.Group,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
): void {
  const tex = getCrosswalkTexture();
  tex.repeat.set(1, 2);
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    roughness: 0.7,
    metalness: 0.0,
  });

  const crosswalkW = roadWidth - 1;
  const crosswalkL = 3;
  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;

  for (let iz = 0; iz < grid; iz++) {
    for (let ix = 0; ix < grid; ix++) {
      const cx = -half + roadWithSidewalk + blockW / 2 + ix * (blockW + roadWithSidewalk);
      const cz = -half + roadWithSidewalk + blockW / 2 + iz * (blockW + roadWithSidewalk);

      const offsets = [
        { x: -blockW / 2 - roadWithSidewalk / 2, z: 0, rotY: Math.PI / 2 },
        { x: blockW / 2 + roadWithSidewalk / 2, z: 0, rotY: Math.PI / 2 },
        { x: 0, z: -blockW / 2 - roadWithSidewalk / 2, rotY: 0 },
        { x: 0, z: blockW / 2 + roadWithSidewalk / 2, rotY: 0 },
      ];

      for (const off of offsets) {
        const px = cx + off.x;
        const pz = cz + off.z;
        if (Math.abs(px) > half - 20 || Math.abs(pz) > half - 20) continue;

        const mesh = new THREE.Mesh(
          new THREE.PlaneGeometry(crosswalkL, crosswalkW),
          mat,
        );
        mesh.rotation.x = -Math.PI / 2;
        mesh.rotation.z = off.rotY;
        mesh.position.set(px, 0.04, pz);
        root.add(mesh);
      }
    }
  }
}

function addManholes(
  root: THREE.Group,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
  rng: () => number,
): void {
  const tex = getManholeTexture();
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    transparent: true,
    roughness: 0.9,
    metalness: 0.4,
  });

  const manholeSize = 0.6;
  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;

  for (let iz = 0; iz < grid; iz++) {
    for (let ix = 0; ix < grid; ix++) {
      if (rng() > 0.4) continue;

      const cx = -half + roadWithSidewalk + blockW / 2 + ix * (blockW + roadWithSidewalk);
      const cz = -half + roadWithSidewalk + blockW / 2 + iz * (blockW + roadWithSidewalk);

      const roadCenterX = cx - blockW / 2 - roadWithSidewalk / 2;
      const roadCenterZ = cz;

      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(manholeSize, manholeSize),
        mat,
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.rotation.z = rng() * Math.PI * 2;
      mesh.position.set(
        roadCenterX + (rng() - 0.5) * (roadWidth * 0.5),
        0.035,
        roadCenterZ + (rng() - 0.5) * blockW * 0.3,
      );
      root.add(mesh);
    }
  }
}

function addTrafficLights(
  root: THREE.Group,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
  _rng: () => number,
): void {
  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;
  
  const poleMat = new THREE.MeshStandardMaterial({
    color: 0x2a2a2a,
    roughness: 0.6,
    metalness: 0.4,
  });
  
  const housingMat = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a,
    roughness: 0.5,
    metalness: 0.3,
  });

  function createTrafficLight(): THREE.Group {
    const group = new THREE.Group();

    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.09, 0.14, 4.6, 8),
      poleMat,
    );
    pole.position.y = 2.3;
    pole.castShadow = true;
    group.add(pole);

    const baseBoot = new THREE.Mesh(
      new THREE.BoxGeometry(0.45, 0.12, 0.45),
      poleMat,
    );
    baseBoot.position.y = 0.06;
    baseBoot.castShadow = true;
    group.add(baseBoot);

    const mastArm = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.09, 2.85),
      poleMat,
    );
    mastArm.position.set(0, 4.55, 1.42);
    mastArm.castShadow = true;
    group.add(mastArm);

    const housing = new THREE.Mesh(
      new THREE.BoxGeometry(0.52, 1.18, 0.38),
      housingMat,
    );
    housing.position.set(0, 4.52, 2.72);
    housing.castShadow = true;
    group.add(housing);

    const bezelMat = new THREE.MeshStandardMaterial({
      color: 0x0a0a0c,
      roughness: 0.75,
      metalness: 0.2,
    });
    const lensRed = new THREE.MeshPhysicalMaterial({
      color: 0xff2020,
      emissive: 0xff1000,
      emissiveIntensity: 0.55,
      roughness: 0.25,
      metalness: 0.05,
      transparent: true,
      opacity: 0.95,
    });
    const lensYellow = new THREE.MeshPhysicalMaterial({
      color: 0xffcc00,
      emissive: 0xaa8800,
      emissiveIntensity: 0.12,
      roughness: 0.35,
      metalness: 0.05,
    });
    const lensGreen = new THREE.MeshPhysicalMaterial({
      color: 0x00aa44,
      emissive: 0x004422,
      emissiveIntensity: 0.15,
      roughness: 0.35,
      metalness: 0.05,
    });

    const ys = [4.82, 4.52, 4.22];
    const mats = [lensRed, lensYellow, lensGreen];
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.11, 0.018, 6, 16), bezelMat);
      ring.position.set(0, ys[i]!, 2.92);
      group.add(ring);
      const lens = new THREE.Mesh(new THREE.CircleGeometry(0.095, 16), mats[i]!);
      lens.position.set(0, ys[i]!, 2.935);
      group.add(lens);
      const visor = new THREE.Mesh(
        new THREE.BoxGeometry(0.42, 0.06, 0.14),
        housingMat,
      );
      visor.position.set(0, ys[i]! + 0.12, 2.78);
      visor.rotation.x = -0.35;
      group.add(visor);
    }

    const backPlate = new THREE.Mesh(
      new THREE.BoxGeometry(0.48, 1.12, 0.06),
      new THREE.MeshStandardMaterial({ color: 0x252528, roughness: 0.85, metalness: 0.1 }),
    );
    backPlate.position.set(0, 4.52, 2.52);
    group.add(backPlate);

    const pedBox = new THREE.Mesh(
      new THREE.BoxGeometry(0.14, 0.22, 0.08),
      new THREE.MeshStandardMaterial({ color: 0xffdd22, roughness: 0.5, emissive: 0x332200, emissiveIntensity: 0.08 }),
    );
    pedBox.position.set(0.22, 1.1, 0.12);
    group.add(pedBox);

    return group;
  }
  
  for (let iz = 0; iz <= grid; iz++) {
    for (let ix = 0; ix <= grid; ix++) {
      if ((ix + iz) % 3 !== 0) continue;
      
      const cx = -half + ix * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
      const cz = -half + iz * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
      
      if (Math.abs(cx) > half - 50 || Math.abs(cz) > half - 50) continue;
      
      const corners = [
        { x: roadWidth / 2 + MAP.SIDEWALK_WIDTH + 0.5, z: roadWidth / 2 + MAP.SIDEWALK_WIDTH + 0.5, rot: Math.PI * 0.75 },
        { x: -roadWidth / 2 - MAP.SIDEWALK_WIDTH - 0.5, z: roadWidth / 2 + MAP.SIDEWALK_WIDTH + 0.5, rot: Math.PI * 0.25 },
        { x: -roadWidth / 2 - MAP.SIDEWALK_WIDTH - 0.5, z: -roadWidth / 2 - MAP.SIDEWALK_WIDTH - 0.5, rot: -Math.PI * 0.25 },
        { x: roadWidth / 2 + MAP.SIDEWALK_WIDTH + 0.5, z: -roadWidth / 2 - MAP.SIDEWALK_WIDTH - 0.5, rot: -Math.PI * 0.75 },
      ];
      
      for (const corner of corners) {
        const light = createTrafficLight();
        light.position.set(cx + corner.x, 0, cz + corner.z);
        light.rotation.y = corner.rot;
        root.add(light);
      }
    }
  }
}

function addRoadDetails(
  root: THREE.Group,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
  rng: () => number,
): void {
  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;
  
  const oilStainMat = new THREE.MeshStandardMaterial({
    color: 0x0a0a0a,
    roughness: 0.15,
    metalness: 0.2,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    envMapIntensity: 0.8,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });

  const oilRainbowMat = new THREE.MeshStandardMaterial({
    color: 0x2a1a3a,
    roughness: 0.08,
    metalness: 0.4,
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
    envMapIntensity: 1.2,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  
  const patchMat = new THREE.MeshStandardMaterial({
    color: 0x1e1e1e,
    roughness: 0.98,
    metalness: 0.0,
    transparent: true,
    opacity: 0.75,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });

  const patchFreshMat = new THREE.MeshStandardMaterial({
    color: 0x252525,
    roughness: 0.85,
    metalness: 0.02,
    transparent: true,
    opacity: 0.8,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
  
  const crackMat = new THREE.MeshStandardMaterial({
    color: 0x080808,
    roughness: 1.0,
    metalness: 0.0,
    transparent: true,
    opacity: 0.7,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });

  const puddleMat = new THREE.MeshStandardMaterial({
    color: 0x3a4a5a,
    roughness: 0.02,
    metalness: 0.3,
    transparent: true,
    opacity: 0.4,
    depthWrite: false,
    envMapIntensity: 1.5,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  });
  
  for (let i = 0; i <= grid; i++) {
    const roadZ = -half + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    
    for (let x = -half + 30; x < half - 30; x += 12 + rng() * 20) {
      if (rng() > 0.4) continue;
      
      const stainW = 1.0 + rng() * 2.0;
      const stainL = 2.0 + rng() * 4.0;
      const mat = rng() > 0.6 ? oilRainbowMat : oilStainMat;
      const stain = new THREE.Mesh(
        new THREE.PlaneGeometry(stainW, stainL),
        mat,
      );
      stain.rotation.x = -Math.PI / 2;
      stain.rotation.z = rng() * Math.PI * 2;
      stain.position.set(
        x + (rng() - 0.5) * 4,
        0.025,
        roadZ + (rng() - 0.5) * roadWidth * 0.6,
      );
      root.add(stain);
    }
    
    for (let x = -half + 50; x < half - 50; x += 35 + rng() * 50) {
      if (rng() > 0.3) continue;
      
      const patchW = 2.5 + rng() * 5;
      const patchL = 2.5 + rng() * 5;
      const mat = rng() > 0.5 ? patchFreshMat : patchMat;
      const patch = new THREE.Mesh(
        new THREE.PlaneGeometry(patchW, patchL),
        mat,
      );
      patch.rotation.x = -Math.PI / 2;
      patch.position.set(
        x + (rng() - 0.5) * 5,
        0.022,
        roadZ + (rng() - 0.5) * roadWidth * 0.5,
      );
      root.add(patch);
    }
    
    for (let x = -half + 20; x < half - 20; x += 20 + rng() * 30) {
      if (rng() > 0.35) continue;
      
      const crackLen = 1.5 + rng() * 4;
      const crack = new THREE.Mesh(
        new THREE.PlaneGeometry(0.1, crackLen),
        crackMat,
      );
      crack.rotation.x = -Math.PI / 2;
      crack.rotation.z = (rng() - 0.5) * 0.6;
      crack.position.set(
        x + (rng() - 0.5) * 3,
        0.023,
        roadZ + (rng() - 0.5) * roadWidth * 0.7,
      );
      root.add(crack);

      if (rng() > 0.6) {
        const branch = new THREE.Mesh(
          new THREE.PlaneGeometry(0.08, crackLen * 0.5),
          crackMat,
        );
        branch.rotation.x = -Math.PI / 2;
        branch.rotation.z = crack.rotation.z + (rng() > 0.5 ? 0.4 : -0.4);
        branch.position.copy(crack.position);
        branch.position.x += (rng() - 0.5) * 0.5;
        branch.position.z += (rng() - 0.5) * crackLen * 0.3;
        root.add(branch);
      }
    }

    for (let x = -half + 40; x < half - 40; x += 60 + rng() * 80) {
      if (rng() > 0.25) continue;
      
      const puddleW = 1.5 + rng() * 3;
      const puddleL = 2 + rng() * 4;
      const puddle = new THREE.Mesh(
        new THREE.PlaneGeometry(puddleW, puddleL),
        puddleMat,
      );
      puddle.rotation.x = -Math.PI / 2;
      puddle.rotation.z = rng() * Math.PI;
      puddle.position.set(
        x + (rng() - 0.5) * 6,
        0.024,
        roadZ + (rng() - 0.5) * roadWidth * 0.4,
      );
      root.add(puddle);
    }
  }
  
  for (let j = 0; j <= grid; j++) {
    const roadX = -half + j * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    
    for (let z = -half + 30; z < half - 30; z += 15 + rng() * 25) {
      if (rng() > 0.3) continue;
      
      const stainW = 1.5 + rng() * 3;
      const stainL = 0.8 + rng() * 1.5;
      const stain = new THREE.Mesh(
        new THREE.PlaneGeometry(stainW, stainL),
        oilStainMat,
      );
      stain.rotation.x = -Math.PI / 2;
      stain.rotation.z = rng() * Math.PI * 2;
      stain.position.set(
        roadX + (rng() - 0.5) * roadWidth * 0.6,
        0.025,
        z + (rng() - 0.5) * 4,
      );
      root.add(stain);
    }
    
    for (let z = -half + 50; z < half - 50; z += 40 + rng() * 60) {
      if (rng() > 0.2) continue;
      
      const patchW = 2 + rng() * 4;
      const patchL = 2 + rng() * 4;
      const patch = new THREE.Mesh(
        new THREE.PlaneGeometry(patchW, patchL),
        patchMat,
      );
      patch.rotation.x = -Math.PI / 2;
      patch.position.set(
        roadX + (rng() - 0.5) * roadWidth * 0.5,
        0.022,
        z + (rng() - 0.5) * 5,
      );
      root.add(patch);
    }
  }
}

function addStreetFurniture(
  root: THREE.Group,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
  rng: () => number,
): void {
  const poleMat = new THREE.MeshStandardMaterial({
    color: 0x505050,
    roughness: 0.7,
    metalness: 0.3,
  });
  const benchMat = new THREE.MeshStandardMaterial({
    color: 0x5d4037,
    roughness: 0.8,
    metalness: 0.1,
  });
  const binMat = new THREE.MeshStandardMaterial({
    color: 0x2e7d32,
    roughness: 0.6,
    metalness: 0.2,
  });

  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;
  const furnitureSpacing = 40;

  for (let i = 0; i <= grid; i++) {
    const z = -half + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    const swOffset = roadWidth / 2 + MAP.SIDEWALK_WIDTH / 2;

    for (let x = -half + furnitureSpacing; x < half - furnitureSpacing; x += furnitureSpacing) {
      if (rng() > 0.5) continue;

      const side = rng() > 0.5 ? 1 : -1;
      const px = x + (rng() - 0.5) * 10;
      const pz = z + side * swOffset;

      const type = rng();
      if (type < 0.4) {
        // Decorative pole light
        const pole = new THREE.Mesh(
          new THREE.CylinderGeometry(0.08, 0.1, 4, 6),
          poleMat,
        );
        pole.position.set(px, 2, pz);
        pole.castShadow = true;
        root.add(pole);

        const lamp = new THREE.Mesh(
          new THREE.SphereGeometry(0.3, 8, 8),
          new THREE.MeshStandardMaterial({
            color: 0xfff8e1,
            emissive: 0xfff8e1,
            emissiveIntensity: 0.3,
          }),
        );
        lamp.position.set(px, 4.2, pz);
        root.add(lamp);
      } else if (type < 0.7) {
        // Park bench with slats and cast-iron armrests
        const ironMat = new THREE.MeshStandardMaterial({
          color: 0x2a2e35, roughness: 0.55, metalness: 0.5,
        });
        // Cast iron end frames (2 ends)
        for (const ex of [-0.85, 0.85]) {
          // Legs (front + back)
          for (const lz of [-0.18, 0.18]) {
            const leg = new THREE.Mesh(
              new THREE.BoxGeometry(0.06, 0.44, 0.06),
              ironMat,
            );
            leg.position.set(px + ex, 0.22, pz + lz);
            root.add(leg);
          }
          // Armrest
          const arm = new THREE.Mesh(
            new THREE.BoxGeometry(0.08, 0.06, 0.42),
            ironMat,
          );
          arm.position.set(px + ex, 0.56, pz);
          root.add(arm);
          // Curved brace
          const brace = new THREE.Mesh(
            new THREE.BoxGeometry(0.05, 0.3, 0.05),
            ironMat,
          );
          brace.position.set(px + ex, 0.38, pz + (side > 0 ? -0.18 : 0.18));
          brace.rotation.x = side > 0 ? 0.2 : -0.2;
          root.add(brace);
        }
        // Seat slats (5 wooden slats)
        for (let si = -2; si <= 2; si++) {
          const slat = new THREE.Mesh(
            new THREE.BoxGeometry(1.8, 0.04, 0.08),
            benchMat,
          );
          slat.position.set(px, 0.46, pz + si * 0.09);
          slat.castShadow = true;
          root.add(slat);
        }
        // Back slats (4 wooden slats)
        for (let bi = 0; bi < 4; bi++) {
          const bSlat = new THREE.Mesh(
            new THREE.BoxGeometry(1.8, 0.06, 0.035),
            benchMat,
          );
          bSlat.position.set(px, 0.62 + bi * 0.1, pz + (side > 0 ? -0.22 : 0.22));
          bSlat.castShadow = true;
          root.add(bSlat);
        }
      } else {
        // City wire-mesh trash bin
        const wireMat = new THREE.MeshStandardMaterial({
          color: 0x2a2a2a, roughness: 0.6, metalness: 0.4,
        });
        // Main body cylinder
        const bin = new THREE.Mesh(
          new THREE.CylinderGeometry(0.25, 0.28, 0.75, 8),
          binMat,
        );
        bin.position.set(px, 0.375, pz);
        bin.castShadow = true;
        root.add(bin);
        // Top ring
        const topRing = new THREE.Mesh(
          new THREE.TorusGeometry(0.26, 0.02, 4, 10),
          wireMat,
        );
        topRing.rotation.x = Math.PI / 2;
        topRing.position.set(px, 0.76, pz);
        root.add(topRing);
        // Mid rib
        const midRib = new THREE.Mesh(
          new THREE.TorusGeometry(0.27, 0.015, 4, 10),
          wireMat,
        );
        midRib.rotation.x = Math.PI / 2;
        midRib.position.set(px, 0.45, pz);
        root.add(midRib);
        // Dome lid with opening
        const lid = new THREE.Mesh(
          new THREE.SphereGeometry(0.26, 8, 4, 0, Math.PI * 2, 0, Math.PI * 0.35),
          wireMat,
        );
        lid.position.set(px, 0.76, pz);
        root.add(lid);
        // Mounting post
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.04, 0.04, 1.0, 4),
          poleMat,
        );
        post.position.set(px, 0.5, pz);
        root.add(post);
      }
    }
  }
}

/** Elevated highway — deck + concrete piers + guardrails + lane-edge hints. */
function addElevatedHighway(
  root: THREE.Group,
  half: number,
  deckMat: THREE.MeshStandardMaterial,
): void {
  const mat = deckMat;
  const pierMat = new THREE.MeshStandardMaterial({
    color: 0x6a6e78,
    roughness: 0.88,
    metalness: 0.08,
    envMapIntensity: 0.35,
  });
  const railMat = new THREE.MeshStandardMaterial({
    color: 0x8899aa,
    roughness: 0.45,
    metalness: 0.55,
    envMapIntensity: 0.5,
  });
  const reflectMat = new THREE.MeshStandardMaterial({
    color: 0xffee44,
    roughness: 0.55,
    metalness: 0.15,
    emissive: 0x443300,
    emissiveIntensity: 0.12,
  });

  const y = 8;
  const deckH = 2.5;
  const w = 34;
  const span = half * 1.72;
  const deckBottomY = y - deckH * 0.5;

  function addDeckSegment(
    cx: number,
    cz: number,
    gw: number,
    gd: number,
  ): void {
    const deck = new THREE.Mesh(new THREE.BoxGeometry(gw, deckH, gd), mat);
    deck.position.set(cx, y, cz);
    deck.castShadow = true;
    deck.receiveShadow = true;
    root.add(deck);

    const railH = 1.05;
    const railT = 0.12;
    for (const side of [-1, 1]) {
      const rail = new THREE.Mesh(
        new THREE.BoxGeometry(gw * 0.98, railH, railT),
        railMat,
      );
      rail.position.set(cx, deckBottomY + deckH + railH * 0.5, cz + side * (gd * 0.5 - railT * 0.5));
      rail.castShadow = true;
      root.add(rail);
      for (let t = -0.35; t <= 0.35; t += 0.35) {
        const post = new THREE.Mesh(
          new THREE.BoxGeometry(0.14, railH + 0.15, 0.14),
          railMat,
        );
        post.position.set(cx + t * gw, deckBottomY + deckH + railH * 0.5, cz + side * (gd * 0.5 - 0.02));
        post.castShadow = true;
        root.add(post);
      }
    }

    const stripY = deckBottomY + deckH * 0.5 + 0.02;
    for (const side of [-1, 1]) {
      const strip = new THREE.Mesh(
        new THREE.BoxGeometry(gw * 0.92, 0.04, 0.35),
        reflectMat,
      );
      strip.position.set(cx, stripY, cz + side * (gd * 0.25));
      strip.receiveShadow = true;
      root.add(strip);
    }

    const pierH = Math.max(2.5, deckBottomY - 0.2);
    const pierFoot = 1.85;
    const pierSpanX = Math.min(gw * 0.38, 42);
    const pierSpanZ = Math.min(gd * 0.38, 42);
    const pierXs = [-pierSpanX, pierSpanX];
    const pierZs = [-pierSpanZ, pierSpanZ];
    for (const px of pierXs) {
      for (const pz of pierZs) {
        const wx = cx + px;
        const wz = cz + pz;
        const pier = new THREE.Mesh(
          new THREE.BoxGeometry(pierFoot, pierH, pierFoot),
          pierMat,
        );
        pier.position.set(wx, pierH * 0.5 + 0.1, wz);
        pier.castShadow = true;
        pier.receiveShadow = true;
        root.add(pier);
        const cap = new THREE.Mesh(
          new THREE.BoxGeometry(pierFoot + 0.2, 0.22, pierFoot + 0.2),
          pierMat,
        );
        cap.position.set(wx, pierH + 0.11, wz);
        cap.castShadow = true;
        root.add(cap);
      }
    }
  }

  addDeckSegment(0, half * 0.76, span, w);
  addDeckSegment(0, -half * 0.76, span, w);
  addDeckSegment(half * 0.74, 0, w, span * 0.95);
}

const MARKER_CURB_H = 0.16;
const MARKER_CURB_THICK = 0.2;

function addTowerFootprintCurbRing(
  root: THREE.Group,
  tw: { minX: number; maxX: number; minZ: number; maxZ: number },
  mat: THREE.MeshStandardMaterial,
): void {
  const wX = tw.maxX - tw.minX;
  const wZ = tw.maxZ - tw.minZ;
  const cx = (tw.minX + tw.maxX) * 0.5;
  const cz = (tw.minZ + tw.maxZ) * 0.5;
  const y = MARKER_CURB_H * 0.5;
  const t = MARKER_CURB_THICK;

  const north = new THREE.Mesh(
    new THREE.BoxGeometry(wX + t * 2, MARKER_CURB_H, t),
    mat,
  );
  north.position.set(cx, y, tw.maxZ + t * 0.5);
  north.receiveShadow = true;
  north.castShadow = true;
  root.add(north);

  const south = new THREE.Mesh(
    new THREE.BoxGeometry(wX + t * 2, MARKER_CURB_H, t),
    mat,
  );
  south.position.set(cx, y, tw.minZ - t * 0.5);
  south.receiveShadow = true;
  south.castShadow = true;
  root.add(south);

  const east = new THREE.Mesh(
    new THREE.BoxGeometry(t, MARKER_CURB_H, wZ),
    mat,
  );
  east.position.set(tw.maxX + t * 0.5, y, cz);
  east.receiveShadow = true;
  east.castShadow = true;
  root.add(east);

  const west = new THREE.Mesh(
    new THREE.BoxGeometry(t, MARKER_CURB_H, wZ),
    mat,
  );
  west.position.set(tw.minX - t * 0.5, y, cz);
  west.receiveShadow = true;
  west.castShadow = true;
  root.add(west);
}

/** Low split-rail along park block edges with car-sized gaps toward the middle of each side. */
function addParkPerimeterFences(
  root: THREE.Group,
  outer: { minX: number; maxX: number; minZ: number; maxZ: number },
  mat: THREE.MeshStandardMaterial,
  blockW: number,
): void {
  const gapLen = Math.min(26, blockW * 0.36);
  const seg = (blockW - gapLen) / 2;
  const railH = 0.38;
  const railD = 0.14;
  const y = railH * 0.5;
  const inset = 0.55;

  const addBox = (cx: number, cz: number, sx: number, sy: number, sz: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), mat);
    m.position.set(cx, y, cz);
    m.castShadow = true;
    m.receiveShadow = true;
    root.add(m);
  };

  const zN = outer.maxZ - inset;
  const zS = outer.minZ + inset;
  const xW = outer.minX + inset;
  const xE = outer.maxX - inset;

  addBox(outer.minX + seg * 0.5, zN, seg, railH, railD);
  addBox(outer.maxX - seg * 0.5, zN, seg, railH, railD);
  addBox(outer.minX + seg * 0.5, zS, seg, railH, railD);
  addBox(outer.maxX - seg * 0.5, zS, seg, railH, railD);

  addBox(xW, outer.minZ + seg * 0.5, railD, railH, seg);
  addBox(xW, outer.maxZ - seg * 0.5, railD, railH, seg);
  addBox(xE, outer.minZ + seg * 0.5, railD, railH, seg);
  addBox(xE, outer.maxZ - seg * 0.5, railD, railH, seg);
}

function addGrassTowerMarkersAndEdge(
  root: THREE.Group,
  blockW: number,
  roadWidth: number,
  half: number,
  grid: number,
): void {
  const curbMat = new THREE.MeshStandardMaterial({
    color: 0x5a5c54,
    roughness: 0.9,
    metalness: 0.04,
    envMapIntensity: 0.32,
  });
  const parkMat = new THREE.MeshStandardMaterial({
    color: 0x6d8a62,
    roughness: 0.91,
    metalness: 0.02,
    envMapIntensity: 0.3,
  });
  const edgeMat = new THREE.MeshStandardMaterial({
    color: 0x4d515a,
    roughness: 0.84,
    metalness: 0.12,
    envMapIntensity: 0.38,
  });

  for (let iz = 0; iz < grid; iz++) {
    for (let ix = 0; ix < grid; ix++) {
      const outer = getBlockCellOuterBounds(ix, iz, blockW, roadWidth, half, grid);
      if (!outer) continue;
      if (isParkLot(ix, iz)) {
        addParkPerimeterFences(root, outer, parkMat, blockW);
      } else {
        const tw = getTowerFootprintBounds(ix, iz, blockW, roadWidth, half);
        if (tw) addTowerFootprintCurbRing(root, tw, curbMat);
      }
    }
  }

  const edgeH = 1.35;
  const edgeT = 0.42;
  const inset = 4.2;
  const span = (half - inset) * 2;
  const ey = edgeH * 0.5;
  const north = new THREE.Mesh(
    new THREE.BoxGeometry(span, edgeH, edgeT),
    edgeMat,
  );
  north.position.set(0, ey, half - inset);
  north.castShadow = true;
  north.receiveShadow = true;
  root.add(north);
  const south = north.clone();
  south.position.set(0, ey, -half + inset);
  root.add(south);
  const east = new THREE.Mesh(
    new THREE.BoxGeometry(edgeT, edgeH, span),
    edgeMat,
  );
  east.position.set(half - inset, ey, 0);
  east.castShadow = true;
  east.receiveShadow = true;
  root.add(east);
  const west = east.clone();
  west.position.set(-half + inset, ey, 0);
  root.add(west);
}

/**
 * District-varied buildings, landmarks, elevated highway, curved boulevards.
 */
export function createCity(seed = 42): THREE.Group {
  invalidateBuildingAABBCache();
  const root = new THREE.Group();
  root.name = 'City';
  const rng = mulberry32(seed);

  const { SIZE, GRID, ROAD_WIDTH, SIDEWALK_WIDTH, HALF } = MAP;
  const totalRoadSpace = (GRID + 1) * (ROAD_WIDTH + SIDEWALK_WIDTH * 2);
  const inner = SIZE - totalRoadSpace;
  const blockW = inner / GRID;
  const edgePad = 12;

  const groundDiff = loadRealTexture('/assets/textures/ground_diff.jpg', 150);
  const groundNormal = loadRealNormalMap('/assets/textures/ground_nor.jpg', 150);
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(SIZE * 1.5, SIZE * 1.5),
    new THREE.MeshStandardMaterial({
      color: 0x6a7a5a,
      map: groundDiff,
      normalMap: groundNormal,
      normalScale: new THREE.Vector2(0.8, 0.8),
      roughness: 0.95,
      metalness: 0.0,
      envMapIntensity: 0.25,
    }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -0.05;
  ground.receiveShadow = true;
  root.add(ground);

  /**
   * Non-square UV repeat: road quads are ~2000×14 m — square repeat destroyed detail on one axis.
   * Match Poly Haven ~3 m per tile on both axes (see ROAD_ASPHALT_METERS_PER_TILE).
   */
  const M = ROAD_ASPHALT_METERS_PER_TILE;
  const stripSpan = SIZE - edgePad;
  const repAlong = stripSpan / M;
  const repAcross = ROAD_WIDTH / M;

  const texEw = loadAsphalt02RoadSet(repAlong, repAcross);
  const texNs = loadAsphalt02RoadSet(repAcross, repAlong);
  const roadWetness = 0.25;
  const roadSurfEastWest = makeAsphaltRoadMaterial(texEw, roadWetness);
  const roadSurfNorthSouth = makeAsphaltRoadMaterial(texNs, roadWetness);

  const boulevardRoadW = ROAD_WIDTH - 6;
  const boulevardTex = loadAsphalt02RoadSet(1, boulevardRoadW / M);
  const boulevardRoadMat = makeAsphaltRoadMaterial(boulevardTex, roadWetness * 1.2);
  
  const concreteDiff = loadRealTexture('/assets/textures/concrete_diff.jpg', 100);
  const concreteNormal = loadRealNormalMap('/assets/textures/concrete_nor.jpg', 100);
  const concreteRough = loadRealTexture('/assets/textures/concrete_rough.jpg', 100);
  concreteRough.colorSpace = THREE.NoColorSpace;
  
  const swStd = new THREE.MeshStandardMaterial({
    color: 0xdddddd,
    map: concreteDiff,
    normalMap: concreteNormal,
    normalScale: new THREE.Vector2(1.2, 1.2),
    roughnessMap: concreteRough,
    roughness: 0.9,
    metalness: 0.0,
    envMapIntensity: 0.3,
  });
  
  const deckMat = new THREE.MeshStandardMaterial({
    color: 0x5a6478,
    roughness: 0.6,
    metalness: 0.3,
    roughnessMap: roughRepeat(16, 10),
    envMapIntensity: 0.58,
  });

  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  
  for (let i = 0; i <= GRID; i++) {
    const z = -HALF + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    
    const sidewalkLeft = new THREE.Mesh(
      new THREE.PlaneGeometry(SIZE - edgePad, SIDEWALK_WIDTH),
      swStd,
    );
    sidewalkLeft.rotation.x = -Math.PI / 2;
    sidewalkLeft.position.set(0, 0.08, z - ROAD_WIDTH / 2 - SIDEWALK_WIDTH / 2);
    sidewalkLeft.receiveShadow = true;
    root.add(sidewalkLeft);
    
    const sidewalkRight = new THREE.Mesh(
      new THREE.PlaneGeometry(SIZE - edgePad, SIDEWALK_WIDTH),
      swStd,
    );
    sidewalkRight.rotation.x = -Math.PI / 2;
    sidewalkRight.position.set(0, 0.08, z + ROAD_WIDTH / 2 + SIDEWALK_WIDTH / 2);
    sidewalkRight.receiveShadow = true;
    root.add(sidewalkRight);
    
    const strip = new THREE.Mesh(
      roadPlaneGeometry(SIZE - edgePad, ROAD_WIDTH),
      roadSurfEastWest,
    );
    strip.rotation.x = -Math.PI / 2;
    strip.position.set(0, 0.02, z);
    root.add(strip);
  }
  for (let j = 0; j <= GRID; j++) {
    const x = -HALF + j * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    
    const sidewalkTop = new THREE.Mesh(
      new THREE.PlaneGeometry(SIDEWALK_WIDTH, SIZE - edgePad),
      swStd,
    );
    sidewalkTop.rotation.x = -Math.PI / 2;
    sidewalkTop.position.set(x - ROAD_WIDTH / 2 - SIDEWALK_WIDTH / 2, 0.08, 0);
    sidewalkTop.receiveShadow = true;
    root.add(sidewalkTop);
    
    const sidewalkBottom = new THREE.Mesh(
      new THREE.PlaneGeometry(SIDEWALK_WIDTH, SIZE - edgePad),
      swStd,
    );
    sidewalkBottom.rotation.x = -Math.PI / 2;
    sidewalkBottom.position.set(x + ROAD_WIDTH / 2 + SIDEWALK_WIDTH / 2, 0.08, 0);
    sidewalkBottom.receiveShadow = true;
    root.add(sidewalkBottom);
    
    const strip = new THREE.Mesh(
      roadPlaneGeometry(ROAD_WIDTH, SIZE - edgePad),
      roadSurfNorthSouth,
    );
    strip.rotation.x = -Math.PI / 2;
    strip.position.set(x, 0.02, 0);
    strip.receiveShadow = true;
    root.add(strip);
  }

  addCurvedBoulevards(root, HALF, ROAD_WIDTH, boulevardRoadMat, swStd);
  addElevatedHighway(root, HALF, deckMat);
  addUnderpassFillLights(root, HALF);
  addDistrictAmbientLights(root, HALF);
  addJumpRampMeshes(root);

  addBuildingsInstanced(root, blockW, ROAD_WIDTH, HALF, GRID, rng);
  addRoadCenterStripes(root, blockW, ROAD_WIDTH, HALF, GRID);
  addCurbs(root, blockW, ROAD_WIDTH, HALF, GRID);
  addCrosswalks(root, blockW, ROAD_WIDTH, HALF, GRID);
  addManholes(root, blockW, ROAD_WIDTH, HALF, GRID, rng);
  addRoadDetails(root, blockW, ROAD_WIDTH, HALF, GRID, rng);
  addTrafficLights(root, blockW, ROAD_WIDTH, HALF, GRID, rng);
  addStreetFurniture(root, blockW, ROAD_WIDTH, HALF, GRID, rng);
  addCornerLamps(root, HALF);
  addGrassTowerMarkersAndEdge(root, blockW, ROAD_WIDTH, HALF, GRID);

  root.userData.landmarkSpin = addLandmarks(root, HALF);

  const destruct = createDestructibles(seed);
  root.add(destruct.group);
  root.userData.destructibleInstances = destruct.instances;

  return root;
}
